/**
 * Game type definitions and metadata
 */
/**
 * Available game types
 *
 * Naming convention:
 * - Hide and Seek: "hns"
 * - FP Race: "fpr"
 * - Trailblazers: "trl"
 * - Hide and Seek Teams: "hnst"
 */
export var GameType;
(function (GameType) {
    GameType["HIDE_AND_SEEK"] = "hns";
    GameType["FP_RACE"] = "fpr";
    GameType["TRAILBLAZERS"] = "trl";
    GameType["HIDE_AND_SEEK_TEAMS"] = "hnst";
})(GameType || (GameType = {}));
/**
 * Registry of game metadata for all game types
 */
export const GAME_METADATA = {
    [GameType.HIDE_AND_SEEK]: {
        id: GameType.HIDE_AND_SEEK,
        title: 'Hide and Seek',
        description: 'Classic hide and seek with seekers hunting hiders across the galaxy',
        icon: '🙈',
        color: '#FF6B6B',
        minPlayers: 2,
    },
    [GameType.FP_RACE]: {
        id: GameType.FP_RACE,
        title: 'FP Race',
        description: 'Race through Flashpoints competing for the fastest completion time',
        icon: '🏁',
        color: '#4ECDC4',
        minPlayers: 1,
    },
    [GameType.TRAILBLAZERS]: {
        id: GameType.TRAILBLAZERS,
        title: 'Trailblazers',
        description: 'Teams identify where screenshots were taken across the galaxy and dig for treasure at the exact spot',
        icon: '🧭',
        color: '#F4A259',
        minPlayers: 2,
    },
    [GameType.HIDE_AND_SEEK_TEAMS]: {
        id: GameType.HIDE_AND_SEEK_TEAMS,
        title: 'Hide and Seek Teams',
        description: 'Rotworms vs Frogdogs: the teams take turns to hide, and the team that stays hidden longest on average wins',
        icon: '🐸',
        color: '#ae7cff',
        minPlayers: 2,
    },
};
/**
 * Get metadata for a game type
 */
export function getGameMetadata(gameType) {
    return GAME_METADATA[gameType];
}
/**
 * Types of cheating that can be detected
 */
export var CheatType;
(function (CheatType) {
    CheatType["STEALTH"] = "stealth";
    CheatType["TELEPORT"] = "teleport";
    CheatType["QUICK_TRAVEL"] = "quick-travel";
    CheatType["TAXI"] = "taxi";
    CheatType["LEFT_AREA"] = "left-area";
    CheatType["INTERCEDE"] = "intercede";
    CheatType["PULL"] = "pull";
    CheatType["KNOCKBACK"] = "knockback";
    CheatType["LEAP"] = "leap";
    CheatType["DEATH"] = "death";
    /** Raised the SWTOR Max Camera Distance over the round's limit and didn't lower it in time. */
    CheatType["CAMERA_DISTANCE"] = "camera-distance";
    /** Used one of the mounts that hide the player's model and make them untaggable. Always disqualifies. */
    CheatType["BANNED_MOUNT"] = "banned-mount";
    CheatType["MANUAL"] = "manual";
})(CheatType || (CheatType = {}));
/**
 * Get the display text for a cheat type
 */
export function getCheatTypeDisplayText(cheatType) {
    switch (cheatType) {
        case CheatType.STEALTH:
            return 'Stealth';
        case CheatType.TELEPORT:
            return 'Teleport';
        case CheatType.QUICK_TRAVEL:
            return 'Quick Travel';
        case CheatType.TAXI:
            return 'Taxi';
        case CheatType.LEFT_AREA:
            return 'Left Area';
        case CheatType.INTERCEDE:
            return 'Intercede';
        case CheatType.PULL:
            return 'Pull';
        case CheatType.KNOCKBACK:
            return 'Knockback';
        case CheatType.LEAP:
            return 'Leap';
        case CheatType.DEATH:
            return 'Death';
        case CheatType.CAMERA_DISTANCE:
            return 'Camera Distance';
        case CheatType.BANNED_MOUNT:
            return 'Banned Mount';
        case CheatType.MANUAL:
            return 'Manual';
        default:
            return 'Unknown';
    }
}
/**
 * Whether a game mode requires players to complete registration data before
 * the admin can advance to pool_creation.
 */
export const GAME_MODE_REQUIRES_REGISTRATION = {
    [GameType.FP_RACE]: true,
    [GameType.HIDE_AND_SEEK]: false,
    [GameType.TRAILBLAZERS]: false,
    [GameType.HIDE_AND_SEEK_TEAMS]: false,
};
